import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Check, 
  Copy, 
  Calendar, 
  MapPin, 
  Clock, 
  Ticket,
  Instagram,
  Heart,
  Mail,
  Share2,
  MessageCircle
} from "lucide-react";

const ThankYouPage = () => {
  const [copied, setCopied] = useState(false);

  const shareMessage = `Assalamu alaikum 💛 Lass zusammen zum Hijab & Halal Pop-up von EDYT & BLOOM in Hamburg! So, 08.02.2026 (11–19 Uhr), Grand Palais. Eintritt 5€ bar. Für Schwestern. Kommst du mit?`;

  const handleCopy = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareMessage);
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement("textarea");
        textArea.value = shareMessage;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success("Nachricht kopiert!");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Kopieren fehlgeschlagen - bitte manuell kopieren");
    }
  };

  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body">
      {/* Hero Section */}
      <section className="px-4 py-12 md:py-20">
        <div className="max-w-lg mx-auto">
          {/* Success Icon */}
          <div 
            className="w-20 h-20 rounded-full bg-[#8B2332] flex items-center justify-center mx-auto mb-8 animate-scale-in"
            data-testid="success-icon"
          >
            <Check className="w-10 h-10 text-white" />
          </div>

          {/* Headline */}
          <h1 
            className="font-heading text-4xl md:text-5xl text-[#2D2D2D] text-center mb-4 animate-fade-in-up"
            data-testid="thank-you-headline"
          >
            Du bist drin 💛
          </h1>

          <p className="text-[#6B6B6B] text-center text-lg mb-12 animate-fade-in-up stagger-1">
            Dein Schwestern-Pass ist gesichert.
          </p>

          {/* Next Steps Card */}
          <div 
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5] mb-8 animate-fade-in-up stagger-2"
            data-testid="next-steps-card"
          >
            <h2 className="font-heading text-2xl text-[#2D2D2D] mb-6">
              Was jetzt?
            </h2>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#8B2332]/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#8B2332]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#2D2D2D] mb-1">
                    Schau in dein Postfach
                  </h3>
                  <p className="text-[#6B6B6B] text-sm">
                    Bitte bestätige deine E-Mail, falls du eine Bestätigung bekommst.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#8B2332]/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#8B2332]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#2D2D2D] mb-1">
                    Speichere dir diese Infos
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF7F2] rounded-full text-xs text-[#2D2D2D] border border-[#EFEBE5]">
                      <Calendar className="w-3 h-3 text-[#8B2332]" />
                      So, 08.02.2026
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF7F2] rounded-full text-xs text-[#2D2D2D] border border-[#EFEBE5]">
                      <Clock className="w-3 h-3 text-[#8B2332]" />
                      11–19 Uhr
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF7F2] rounded-full text-xs text-[#2D2D2D] border border-[#EFEBE5]">
                      <MapPin className="w-3 h-3 text-[#8B2332]" />
                      Grand Palais, Hamburg
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF7F2] rounded-full text-xs text-[#2D2D2D] border border-[#EFEBE5]">
                      <Ticket className="w-3 h-3 text-[#8B2332]" />
                      5€ bar
                    </span>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#8B2332]/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-[#8B2332]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#2D2D2D] mb-1">
                    Kein-Bock-zu-frieren-Kasse
                  </h3>
                  <p className="text-[#6B6B6B] text-sm">
                    Zeig am Eingang deinen Pass (Mail/Screenshot), wenn es voll ist.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Share Block */}
          <div 
            className="bg-[#8B2332] rounded-2xl p-6 md:p-8 text-white mb-8 animate-fade-in-up stagger-3"
            data-testid="share-block"
          >
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="w-5 h-5" />
              <h2 className="font-heading text-2xl">
                Bring 1 Schwester mit
              </h2>
            </div>

            <p className="text-white/90 mb-6">
              Schick das an 1 Schwester, mit der du hingehen willst.
            </p>

            {/* Message Preview */}
            <div className="bg-white/10 rounded-xl p-4 mb-4">
              <p className="text-white/90 text-sm leading-relaxed">
                {shareMessage}
              </p>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleCopy}
                className="flex-1 bg-white text-[#8B2332] hover:bg-[#FAF7F2] rounded-full h-12 font-medium transition-all"
                data-testid="copy-button"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Kopiert!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Nachricht kopieren
                  </>
                )}
              </Button>

              <Button
                onClick={handleWhatsAppShare}
                className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full h-12 font-medium transition-all"
                data-testid="whatsapp-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>

          {/* Follow Block */}
          <div 
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5] text-center animate-fade-in-up stagger-4"
            data-testid="follow-block"
          >
            <Instagram className="w-10 h-10 text-[#8B2332] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#2D2D2D] mb-2">
              Folge @edytandbloom
            </h2>
            <p className="text-[#6B6B6B] text-sm mb-6">
              Für Updates und Behind-the-Scenes.
            </p>

            <a
              href="https://www.instagram.com/edytandbloom/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button
                className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white rounded-full px-8 h-12 font-medium transition-all"
                data-testid="instagram-follow-button"
              >
                <Instagram className="w-4 h-4 mr-2" />
                Auf Instagram folgen
              </Button>
            </a>

            <p className="text-[#6B6B6B] text-xs mt-4">
              Schreib uns „POPUP" in DM, wenn du Fragen hast.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-[#EFEBE5]" data-testid="footer-thank-you">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-[#6B6B6B]">
              <Heart className="w-4 h-4 text-[#8B2332]" />
              <span className="font-medium text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a 
                href="/impressum" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-impressum-danke"
              >
                Impressum
              </a>
              <a 
                href="/datenschutz" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-datenschutz-danke"
              >
                Datenschutz
              </a>
              <a 
                href="https://www.instagram.com/edytandbloom/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-instagram-danke"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage;
